import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Users,
  Briefcase,
  FileText,
  Euro,
  TrendingUp,
  Calendar,
  Clock,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

export default function DashboardPage() {
  const { user, isManager } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await api.get('/dashboard/stats');
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const chartData = stats?.monthly_revenue?.map((item, index) => ({
    name: MONTHS[index],
    revenue: item.revenue,
  })) || [];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              Bonjour, {user?.name?.split(' ')[0]} 👋
            </h1>
            <p className="text-slate-500 mt-1">
              Voici un aperçu de votre activité
            </p>
          </div>
          <Badge variant="outline" className="self-start">
            {new Date().toLocaleDateString('fr-FR', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </Badge>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {isManager && (
            <>
              <Card className="card-hover" data-testid="stat-clients">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-500">Clients</p>
                      <p className="text-3xl font-bold text-slate-900 mt-1">
                        {stats?.total_clients || 0}
                      </p>
                    </div>
                    <div className="h-12 w-12 bg-sky-100 rounded-xl flex items-center justify-center">
                      <Users className="h-6 w-6 text-sky-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-hover" data-testid="stat-consultants">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-500">Consultants</p>
                      <p className="text-3xl font-bold text-slate-900 mt-1">
                        {stats?.total_consultants || 0}
                      </p>
                    </div>
                    <div className="h-12 w-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                      <Briefcase className="h-6 w-6 text-emerald-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}

          <Card className="card-hover" data-testid="stat-missions">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Missions actives</p>
                  <p className="text-3xl font-bold text-slate-900 mt-1">
                    {stats?.active_missions || 0}
                  </p>
                </div>
                <div className="h-12 w-12 bg-violet-100 rounded-xl flex items-center justify-center">
                  <Calendar className="h-6 w-6 text-violet-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {isManager && (
            <Card className="card-hover" data-testid="stat-revenue">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">CA Total</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">
                      {formatCurrency(stats?.total_revenue || 0)}
                    </p>
                  </div>
                  <div className="h-12 w-12 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Euro className="h-6 w-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Charts and Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Revenue Chart */}
          {isManager && (
            <Card className="lg:col-span-2" data-testid="revenue-chart">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Chiffre d'affaires mensuel</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#0F172A" stopOpacity={0.1} />
                          <stop offset="95%" stopColor="#0F172A" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="name" stroke="#64748B" fontSize={12} />
                      <YAxis stroke="#64748B" fontSize={12} tickFormatter={(value) => `${value / 1000}k€`} />
                      <Tooltip
                        formatter={(value) => [formatCurrency(value), 'CA']}
                        contentStyle={{
                          backgroundColor: '#fff',
                          border: '1px solid #E2E8F0',
                          borderRadius: '8px',
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="revenue"
                        stroke="#0F172A"
                        strokeWidth={2}
                        fill="url(#colorRevenue)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Stats */}
          <Card data-testid="quick-actions">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Actions rapides</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Clock className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">CRA en attente</p>
                    <p className="text-sm text-slate-500">À valider</p>
                  </div>
                </div>
                <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                  {stats?.pending_cras || 0}
                </Badge>
              </div>

              {isManager && (
                <>
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <FileText className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Factures en cours</p>
                        <p className="text-sm text-slate-500">Non payées</p>
                      </div>
                    </div>
                    <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                      {stats?.pending_invoices || 0}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                        <TrendingUp className="h-5 w-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Pipeline</p>
                        <p className="text-sm text-slate-500">Valeur pondérée</p>
                      </div>
                    </div>
                    <span className="font-semibold text-emerald-600">
                      {formatCurrency(stats?.pipeline_value || 0)}
                    </span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
